import { generateClient } from 'aws-amplify/api';
import { uploadData, getUrl } from '@aws-amplify/storage';
import {
  listFoldersByClient,
  getFolderWithContents,
  listFolderItems,
  checkItemInFolders,
  createFolder,
  updateFolder,
  deleteFolder,
  addItemToFolder,
  removeItemFromFolder,
  logFolderActivity,
  getFolderActivities,
} from 'graphql/fileCabinet';
import { getFILECABINETITEM, getUSER } from 'graphql/queries';

const API = generateClient();

// Folder operations
export const fileCabinetService = {
  // Get all folders for a client
  async getFolders(clientID, parentFolderID = null) {
    try {
      const filter = parentFolderID 
        ? { parentFolderID: { eq: parentFolderID } }
        : { parentFolderID: { attributeExists: false } };
      
      const response = await API.graphql({
        query: listFoldersByClient,
        variables: {
          clientID,
          limit: 1000,
        },
      });
      
      return response.data.foldersByClient.items || [];
    } catch (error) {
      console.error('Error fetching folders:', error);
      throw error;
    }
  },

  // Get folder with its contents
  async getFolderContents(folderID) {
    try {
      const response = await API.graphql({
        query: getFolderWithContents,
        variables: { id: folderID },
      });
      
      return response.data.getFILECABINETFOLDER;
    } catch (error) {
      console.error('Error fetching folder contents:', error);
      throw error;
    }
  },

  // Create a new folder
  async createFolder(folderData) {
    try {
      // Check folder count limit (500 per client)
      const existingFolders = await this.getFolders(folderData.clientID);
      if (existingFolders.length >= 500) {
        throw new Error('Maximum folder limit (500) reached for this client');
      }
      
      // Check nesting depth
      if (folderData.parentFolderID) {
        const parentFolder = await this.getFolderContents(folderData.parentFolderID);
        if (parentFolder && parentFolder.depth >= 4) {
          throw new Error('Maximum nesting depth (5 levels) reached');
        }
        folderData.depth = (parentFolder.depth || 0) + 1;
      } else {
        folderData.depth = 0;
      }
      
      const input = {
        ...folderData,
        itemCount: 0,
        lastActivityAt: new Date().toISOString(),
      };
      
      const response = await API.graphql({
        query: createFolder,
        variables: { input },
      });
      
      // Log activity
      await this.logActivity({
        action: 'CREATE_FOLDER',
        folderID: response.data.createFILECABINETFOLDER.id,
        itemName: folderData.name,
        performedBy: folderData.createdBy,
        clientID: folderData.clientID,
        organizationID: folderData.organizationID,
      });
      
      return response.data.createFILECABINETFOLDER;
    } catch (error) {
      console.error('Error creating folder:', error);
      throw error;
    }
  },

  // Update folder
  async updateFolder(folderID, updates, userID) {
    try {
      const input = {
        id: folderID,
        ...updates,
        lastActivityAt: new Date().toISOString(),
      };
      
      const response = await API.graphql({
        query: updateFolder,
        variables: { input },
      });
      
      // Log activity
      await this.logActivity({
        action: 'RENAME_FOLDER',
        folderID,
        itemName: updates.name,
        performedBy: userID,
        details: JSON.stringify(updates),
        clientID: response.data.updateFILECABINETFOLDER.clientID,
        organizationID: response.data.updateFILECABINETFOLDER.organizationID,
      });
      
      return response.data.updateFILECABINETFOLDER;
    } catch (error) {
      console.error('Error updating folder:', error);
      throw error;
    }
  },

  // Delete folder and all its contents
  async deleteFolder(folderID, userID, clientID, organizationID) {
    try {
      // Get folder details before deletion for the activity log
      let folderName = 'Unknown folder';
      try {
        const folderDetails = await this.getFolderContents(folderID);
        if (folderDetails) {
          folderName = folderDetails.name || 'Unknown folder';
        }
      } catch (err) {
        console.warn('Could not fetch folder details before deletion:', err);
      }
      
      // First, get all items in the folder
      const folderItems = await API.graphql({
        query: listFolderItems,
        variables: { folderID, limit: 1000 },
      });
      
      // Delete all items in the folder
      const deletePromises = folderItems.data.itemsByFolder.items.map(item =>
        API.graphql({
          query: removeItemFromFolder,
          variables: { input: { id: item.id } },
        })
      );
      
      await Promise.all(deletePromises);
      
      // Delete the folder
      const response = await API.graphql({
        query: deleteFolder,
        variables: { input: { id: folderID } },
      });
      
      // Log activity with folder name
      await this.logActivity({
        action: 'DELETE_FOLDER',
        folderID,
        itemName: folderName,
        itemType: 'folder',
        performedBy: userID,
        clientID,
        organizationID,
      });
      
      return response.data.deleteFILECABINETFOLDER;
    } catch (error) {
      console.error('Error deleting folder:', error);
      throw error;
    }
  },

  // Add document/certificate to folder
  async addToFolder(folderID, item, userID, clientID, organizationID) {
    try {
      // Check if item already exists in this specific folder or any folder
      let alreadyInFolders = [];
      let alreadyInThisFolder = false;
      
      try {
        const existingItems = await API.graphql({
          query: checkItemInFolders,
          variables: { 
            certificateID: item.certificateID || item.id,
            limit: 100,
          },
        });
        
        if (existingItems?.data?.itemsByCertificate?.items) {
          alreadyInFolders = existingItems.data.itemsByCertificate.items;
          
          // Check if already in this specific folder
          alreadyInThisFolder = alreadyInFolders.some(folderItem => 
            folderItem.folderID === folderID
          );
          
          if (alreadyInThisFolder) {
            console.warn('Item already exists in this folder, skipping duplicate addition');
            return {
              alreadyInFolders: alreadyInFolders.length,
              duplicate: true,
              message: 'Item already exists in this folder'
            };
          }
        }
      } catch (checkError) {
        // If check fails, continue anyway
        console.warn('Could not check existing folders:', checkError);
      }
      
      const input = {
        folderID,
        certificateID: item.certificateID || item.id,
        documentID: item.documentID,
        documentType: item.documentType || item.type || 'certificate',
        documentName: item.name,
        clientID,
        addedBy: userID,
      };
      
      const response = await API.graphql({
        query: addItemToFolder,
        variables: { input },
      });
      
      // Get current folder to update item count
      let currentItemCount = 0;
      try {
        const folderData = await this.getFolderContents(folderID);
        currentItemCount = folderData?.itemCount || 0;
      } catch (e) {
        console.warn('Could not get folder item count:', e);
      }
      
      // Update folder's item count and last activity
      try {
        await API.graphql({
          query: updateFolder,
          variables: {
            input: {
              id: folderID,
              itemCount: currentItemCount + 1,
              lastActivityAt: new Date().toISOString(),
            },
          },
        });
      } catch (updateError) {
        console.warn('Could not update folder count:', updateError);
      }
      
      // Log activity
      await this.logActivity({
        action: 'ADD_TO_FOLDER',
        folderID,
        itemID: item.id,
        itemType: item.type || 'certificate',
        itemName: item.name,
        performedBy: userID,
        clientID,
        organizationID,
        details: JSON.stringify({
          alreadyInFolderCount: alreadyInFolders.length,
        }),
      });
      
      return {
        ...response.data.createFILECABINETITEM,
        alreadyInFolders: alreadyInFolders.length,
      };
    } catch (error) {
      console.error('Error adding item to folder:', error);
      if (error.errors && error.errors[0]) {
        console.error('GraphQL Error details:', error.errors[0]);
      }
      throw error;
    }
  },

  // Remove document/certificate from folder
  async removeFromFolder(itemID, folderID, userID, clientID, organizationID, itemData = null) {
    try {
      // If we have item data passed in, use it for the activity log
      let itemName = itemData?.name || itemData?.documentName || 'Unknown item';
      let itemType = itemData?.type || itemData?.documentType || 'document';
      
      // If no item data passed, try to get it before deletion
      if (!itemData) {
        try {
          const getItemResponse = await API.graphql({
            query: getFILECABINETITEM,
            variables: { id: itemID },
          });
          if (getItemResponse?.data?.getFILECABINETITEM) {
            const item = getItemResponse.data.getFILECABINETITEM;
            itemName = item.documentName || item.name || 'Unknown item';
            itemType = item.documentType || item.type || 'document';
          }
        } catch (err) {
          console.warn('Could not fetch item details before removal:', err);
        }
      }
      
      const response = await API.graphql({
        query: removeItemFromFolder,
        variables: { input: { id: itemID } },
      });
      
      // Get current folder to update item count
      const folderData = await this.getFolderContents(folderID);
      const currentItemCount = folderData?.itemCount || 1;
      
      // Update folder's item count and last activity
      await API.graphql({
        query: updateFolder,
        variables: {
          input: {
            id: folderID,
            itemCount: Math.max(0, currentItemCount - 1),
            lastActivityAt: new Date().toISOString(),
          },
        },
      });
      
      // Log activity with item details
      await this.logActivity({
        action: 'REMOVE_FROM_FOLDER',
        folderID,
        itemID,
        itemName,
        itemType,
        performedBy: userID,
        clientID,
        organizationID,
      });
      
      return response.data.deleteFILECABINETITEM;
    } catch (error) {
      console.error('Error removing item from folder:', error);
      throw error;
    }
  },

  // Move folder to different parent
  async moveFolder(folderID, newParentID, userID, clientID, organizationID) {
    try {
      // Get folder details for the activity log
      let folderName = 'Unknown folder';
      try {
        const folderDetails = await this.getFolderContents(folderID);
        if (folderDetails) {
          folderName = folderDetails.name || 'Unknown folder';
        }
      } catch (err) {
        console.warn('Could not fetch folder details:', err);
      }
      
      // Check new depth
      let newDepth = 0;
      if (newParentID) {
        const newParent = await this.getFolderContents(newParentID);
        newDepth = (newParent.depth || 0) + 1;
        if (newDepth >= 5) {
          throw new Error('Moving folder would exceed maximum depth (5 levels)');
        }
      }
      
      const input = {
        id: folderID,
        parentFolderID: newParentID,
        depth: newDepth,
        lastActivityAt: new Date().toISOString(),
      };
      
      const response = await API.graphql({
        query: updateFolder,
        variables: { input },
      });
      
      // Log activity with folder name
      await this.logActivity({
        action: 'MOVE_FOLDER',
        folderID,
        itemName: folderName,
        itemType: 'folder',
        performedBy: userID,
        clientID,
        organizationID,
        details: JSON.stringify({ newParentID, newDepth }),
      });
      
      return response.data.updateFILECABINETFOLDER;
    } catch (error) {
      console.error('Error moving folder:', error);
      throw error;
    }
  },

  // Log activity
  async logActivity(activityData) {
    try {
      const input = {
        ...activityData,
        createdAt: new Date().toISOString(),
      };
      
      console.log('Logging activity with input:', input);
      
      await API.graphql({
        query: logFolderActivity,
        variables: { input },
      });
      
      console.log('Activity logged successfully');
    } catch (error) {
      console.error('Error logging activity:', error);
      
      // Log more details about what went wrong
      if (error.errors) {
        error.errors.forEach((err, index) => {
          console.error(`GraphQL Error ${index + 1}:`, err.message);
          if (err.path) {
            console.error('Path:', err.path);
          }
          if (err.locations) {
            console.error('Locations:', err.locations);
          }
        });
      }
      
      console.log('Input that failed:', activityData);
      
      // Don't throw - logging failure shouldn't stop the main operation
    }
  },

  // Get activity log
  async getActivityLog(clientID, limit = 50) {
    try {
      const response = await API.graphql({
        query: getFolderActivities,
        variables: {
          clientID,
          limit,
          createdAt: {
            le: new Date().toISOString(),
          },
        },
      });
      
      const activities = response.data.activitiesByClient.items || [];
      
      // Fetch user information for each activity
      const activitiesWithUsers = await Promise.all(
        activities.map(async (activity) => {
          try {
            if (activity.performedBy) {
              const userResponse = await API.graphql({
                query: getUSER,
                variables: { id: activity.performedBy },
              });
              
              if (userResponse?.data?.getUSER) {
                return {
                  ...activity,
                  performedByUser: userResponse.data.getUSER,
                };
              }
            }
          } catch (err) {
            console.warn(`Could not fetch user details for ${activity.performedBy}:`, err);
          }
          
          return activity;
        })
      );
      
      return activitiesWithUsers;
    } catch (error) {
      console.error('Error fetching activity log:', error);
      throw error;
    }
  },

  // Upload file to folder
  async uploadFileToFolder(file, folder, userID, clientID, organizationID) {
    try {
      // Generate unique file name to avoid conflicts
      const timestamp = new Date().getTime();
      const fileExtension = file.name.split('.').pop();
      const baseName = file.name.replace(`.${fileExtension}`, '');
      const fileName = `${baseName}_${timestamp}.${fileExtension}`;
      
      // Upload file to S3 in the file cabinet directory
      const filePath = `protected/file-cabinet/${clientID}/${folder.id}/${fileName}`;
      
      console.log('Uploading file to path:', filePath);
      
      const uploadResult = await uploadData({
        path: filePath,
        data: file,
        options: {
          contentType: file.type || 'application/octet-stream',
        }
      });
      
      console.log('File uploaded successfully:', uploadResult);
      
      // Create a document record that can be added to the folder
      const documentItem = {
        id: `file_${timestamp}`, // Temporary ID for the uploaded file
        certificateID: `file_${timestamp}`, // Use same ID
        documentID: `file_${timestamp}`,
        name: file.name,
        type: 'document',
        documentType: 'uploaded_file',
        filePath: filePath,
        fileSize: file.size,
        mimeType: file.type,
        uploadedAt: new Date().toISOString(),
      };
      
      // Add the file to the folder using existing addToFolder method
      const folderItemResult = await this.addToFolder(
        folder.id,
        documentItem,
        userID,
        clientID,
        organizationID
      );
      
      // Log the file upload activity
      await this.logActivity({
        action: 'UPLOAD_FILE',
        folderID: folder.id,
        itemID: documentItem.id,
        itemType: 'uploaded_file',
        itemName: file.name,
        performedBy: userID,
        clientID,
        organizationID,
        details: JSON.stringify({
          fileName: file.name,
          fileSize: file.size,
          filePath: filePath,
        }),
      });
      
      return {
        ...folderItemResult,
        uploadResult,
        filePath,
        documentItem,
      };
    } catch (error) {
      console.error('Error uploading file to folder:', error);
      throw error;
    }
  },

  // Get uploaded file URL
  async getUploadedFileUrl(filePath) {
    try {
      const { url } = await getUrl({
        path: filePath,
      });
      return url.href;
    } catch (error) {
      console.error('Error getting file URL:', error);
      throw error;
    }
  },

  // Get folder paths for a document/certificate
  async getDocumentFolderPaths(certificateID, clientID) {
    try {
      if (!certificateID) return [];

      // Find all folder items for this certificate
      const response = await API.graphql({
        query: checkItemInFolders,
        variables: { 
          certificateID,
          limit: 100,
        },
      });

      const folderItems = response?.data?.itemsByCertificate?.items || [];
      
      if (folderItems.length === 0) {
        return [];
      }

      // Get all folders for the client to build folder names map
      const allFolders = await this.getFolders(clientID);
      const folderMap = {};
      
      // Build a map of folder ID to folder info
      const buildFolderMap = (folders, path = []) => {
        folders.forEach(folder => {
          folderMap[folder.id] = {
            name: folder.name,
            path: [...path, folder.name].join(' / ')
          };
          
          // Recursively process sub-folders
          if (folder.subFolders && folder.subFolders.length > 0) {
            buildFolderMap(folder.subFolders, [...path, folder.name]);
          }
        });
      };
      
      buildFolderMap(allFolders);

      // Remove duplicate folder items (same document in same folder multiple times)
      const uniqueFolderItems = folderItems.filter((item, index, self) => 
        index === self.findIndex(f => f.folderID === item.folderID)
      );

      // Map folder items to folder paths
      const folderPaths = uniqueFolderItems.map(item => {
        const folderInfo = folderMap[item.folderID];
        return {
          folderID: item.folderID,
          folderName: folderInfo?.name || 'Unknown',
          folderPath: folderInfo?.path || 'Unknown',
        };
      });

      return folderPaths;
    } catch (error) {
      console.error('Error getting document folder paths:', error);
      return [];
    }
  },

  // Get folder paths for multiple documents (batch operation)
  async getDocumentsFolderPaths(certificateIDs, clientID) {
    try {
      const results = {};
      
      // Process in batches to avoid overwhelming the API
      const batchSize = 10;
      for (let i = 0; i < certificateIDs.length; i += batchSize) {
        const batch = certificateIDs.slice(i, i + batchSize);
        
        const batchResults = await Promise.all(
          batch.map(async (certificateID) => {
            const paths = await this.getDocumentFolderPaths(certificateID, clientID);
            return { certificateID, paths };
          })
        );
        
        batchResults.forEach(({ certificateID, paths }) => {
          results[certificateID] = paths;
        });
      }
      
      return results;
    } catch (error) {
      console.error('Error getting documents folder paths:', error);
      return {};
    }
  },

  // Search folders and items
  async searchFoldersAndItems(clientID, searchTerm) {
    try {
      // Get all folders
      const folders = await this.getFolders(clientID);
      
      // Filter folders by search term
      const matchingFolders = folders.filter(folder =>
        folder.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (folder.description && folder.description.toLowerCase().includes(searchTerm.toLowerCase()))
      );
      
      // TODO: Also search items within folders
      
      return {
        folders: matchingFolders,
        items: [], // To be implemented
      };
    } catch (error) {
      console.error('Error searching folders:', error);
      throw error;
    }
  },
};

export default fileCabinetService;